<script setup lang="ts">
import winePlaceholder from '@/assets/images/wine_placeholder.png'
import PlusIcon from '@/components/icons/plusIcon.vue'
import WinemakerCard from '@/components/WinemakerCard.vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { useWineStore } from '@/stores/wine'
import { useWinemakersStore } from '@/stores/winemakers'
import { Winemaker } from '@/types'
import { formatDateRu as formatDateRuUtil } from '@/utils/dates'
import { computed, onMounted, ref, watch } from 'vue'

// const router = useRouter()
const uiStore = useUiStore()
const wineStore = useWineStore()
const winemakersStore = useWinemakersStore()
type RecentWine = {
    id: string
    name: string
    bg_image?: { url: string }
    bottle_image?: { url: string }
    rating?: number
    date?: string
    place?: string
    winemaker?: string
    year?: string
}
const authStore = useAuthStore()

const waitAuthReady = () =>
    new Promise<void>((resolve) => {
        if (!authStore.isLoading) return resolve()
        const stop = watch(
            () => authStore.isLoading,
            (v) => {
                if (!v) {
                    stop()
                    resolve()
                }
            }
        )
    })

const lastScannedWine = computed<RecentWine | null>(() => {
    // Используем первый элемент из списка сканов (самый последний)
    const scannedWines = wineStore.scannedWines
    if (!scannedWines || scannedWines.length === 0) return null

    const scan = scannedWines[0]
    if (!scan || !scan.wine) return null

    return {
        id: scan.id, // Используем ID скана, не вина
        name: scan.wine.name,
        bg_image: scan.wine.bg_image ? { url: scan.wine.bg_image.url } : undefined,
        bottle_image: scan.wine.bottle_image ? { url: scan.wine.bottle_image.url } : undefined,
        rating: scan.review?.rating || scan.wine.average_rating || 0,
        date: scan.review?.place
            ? formatDateRuUtil(scan.created_at) + ','
            : formatDateRuUtil(scan.created_at),
        place: scan.review?.place || '',
        winemaker: scan.winemaker?.name || '',
        year: scan.wine.year ? String(scan.wine.year) : '',
    }
})
const winemakers = computed<Winemaker[]>(() => winemakersStore.winemakers)
const isLoading = ref(false)

const loadRecentScans = async () => {
    try {
        uiStore.beginDataLoad()
        isLoading.value = true
        // Загружаем только 1 последний скан
        const res = await wineStore.loadScans({ limit: 1, page: 1 })

        // Если нет данных, сбрасываем loading
        if (!res.success || !res.data) {
            console.warn('No scanned wine data received')
        }
    } catch (error) {
        console.error('Failed to load recent scans:', error)
        // В случае ошибки сбрасываем loading
        isLoading.value = false
    } finally {
        isLoading.value = false
        uiStore.endDataLoad()
    }
}

const loadWinemakers = async () => {
    try {
        uiStore.beginDataLoad()
        await winemakersStore.loadWinemakers({ limit: 10, page: 1 })
    } catch (error) {
        console.error('Failed to load winemakers:', error)
    } finally {
        uiStore.endDataLoad()
    }
}

onMounted(async () => {
    await waitAuthReady()
    await Promise.all([loadRecentScans(), loadWinemakers()])
})

// Перезагружать после смены пользователя
watch(
    () => authStore.user?.id,
    async () => {
        isLoading.value = true
        await loadRecentScans()
    }
)
</script>

<template>
    <div class="container">
        <section v-if="lastScannedWine && !isLoading" class="recent-wines">
            <h2 class="recent-wines__title title">Последнее сканирование</h2>

            <div class="recent-wines__wrap">
                <RouterLink
                    v-if="lastScannedWine"
                    :key="lastScannedWine.id"
                    :to="{ name: 'wine-info', params: { id: lastScannedWine.id } }"
                    class="recent-wine"
                >
                    <div class="recent-wine__info">
                        <div class="recent-wine__info-wrap">
                            <div class="recent-wine__rating">
                                <RatingStars :rating="lastScannedWine.rating || 0" size="small" />
                            </div>
                            <div class="recent-wine__meta">
                                {{ lastScannedWine.date }}
                                <template v-if="lastScannedWine.place">
                                    <br />
                                    {{ lastScannedWine.place }}
                                </template>
                            </div>
                            <div class="recent-wine__name">
                                Вино
                                <br />
                                {{ lastScannedWine.winemaker }}
                                <br />
                                {{ lastScannedWine.name }}
                                <br />
                                {{ lastScannedWine.year }}
                            </div>
                        </div>
                        <div class="recent-wine__link">Подробнее</div>
                    </div>
                    <div class="recent-wine__img-wrap">
                        <img
                            :src="lastScannedWine.bottle_image?.url || winePlaceholder"
                            alt=""
                            class="recent-wine__img"
                        />
                    </div>
                </RouterLink>
                <RouterLink to="/scan" class="add-winemaker-card">
                    <div class="add-winemaker-card__icon">
                        <PlusIcon />
                    </div>
                    <div class="add-winemaker-card__text">Сканировать еще</div>
                </RouterLink>
            </div>
        </section>

        <section v-else-if="isLoading" class="loading-section">
            <div class="loading-spinner">Загрузка...</div>
        </section>

        <section v-else class="how-it-works">
            <h2 class="how-it-works__title title">Как это работает?</h2>
            <p class="how-it-works__description">
                Узнайте больше о вине,
                <br />
                отсканировав уникальную метку
            </p>
            <img class="how-it-works__img" src="@/assets/images/how_it_works.png" />
            <RouterLink to="/scan" class="how-it-works__btn btn"> Сканировать метку </RouterLink>
        </section>

        <section class="winemakers-list">
            <div class="winemakers-list__title">Кто уже с нами?</div>

            <div class="winemakers-list__wrap">
                <div v-for="wm in winemakers" :key="wm.id" class="winemakers-list__item">
                    <WinemakerCard :winemaker="wm" />
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped lang="stylus">
.container
  padding 16px

.recent-wines
  &__title
    margin-bottom 20px
  &__wrap
    display flex
    gap 12px

.add-winemaker-card
    flex-shrink 0
    width 120px
    height 230px
    background-color var(--app-bg-secondary)
    border-radius 12px
    padding 12px
    display flex
    align-items center
    flex-direction column
    justify-content center
    gap 12px
    text-decoration none
    &__icon
        width 24px
        height 24px
    &__text
        font-size 14px
        line-height 18px
        font-weight 500
        color var(--app-text-grey-color)
        text-align center

.recent-wine
    width 100%
    height 230px
    background-color var(--app-bg-secondary)
    border-radius 12px
    padding 12px 0 12px 12px
    display flex
    align-items stretch
    gap 12px
    text-decoration none
    &__link
        font-size 16px
        line-height 19px
        font-weight 500
        color var(--app-main-accent-color)
        text-decoration none
    &__info
        width 100%
        display flex
        flex-direction column
        justify-content space-between
    &__meta
        margin 4px 0
        font-size 10px
        line-height 12px
        font-weight 400
        color var(--app-text-grey-color)
    &__name
        display block
        display -webkit-box
        -webkit-line-clamp 5
        -webkit-box-orient vertical
        overflow hidden
        text-overflow ellipsis
        max-width 100%
        max-height 95px
        font-size 16px
        line-height 19px
        font-weight 500
        color var(--app-text-color-secondary)
    &__img
        object-fit contain
        width 100%
        height 100%
        &-wrap
            flex-shrink 0
            width 80px
            height 100%

.how-it-works
    display flex
    flex-direction column
    align-items center
    gap 16px
    width 100%
    padding 8px 0 0
    &__img
        flex-shrink 0
        display block
        width 100%
        max-width 400px
        height auto
    &__title
        text-align center
        font-size 20px
        line-height 26px
        font-weight 600
    &__description
        text-align center
        font-size 16px
        line-height 22px
        font-weight 400
        color var(--app-text-color-secondary)

.winemakers-list
    &__title
        margin 24px 0 20px
        font-size 20px
        line-height 26px
        font-weight 600
        color var(--app-text-color)
    &__wrap
        display flex
        flex-direction column
        gap 12px
</style>
